import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shuffle, CheckCircle, Loader2, XCircle } from "lucide-react";
import { paymentsAPI } from "../lib/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus("error");
      return;
    }
    
    try {
      const res = await paymentsAPI.checkStatus(sessionId);
      
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("error");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <Card className="bg-card max-w-md w-full">
        <CardContent className="p-8 text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Shuffle className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>TIREO</span>
          </Link>

          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit' }}>
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Veuillez patienter quelques instants.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit' }}>
                Paiement réussi !
              </h1>
              <p className="text-muted-foreground mb-6">
                Votre paiement a été confirmé. Vous pouvez maintenant lancer votre tirage ou activer votre sondage.
              </p>
              <Button 
                onClick={() => navigate("/dashboard")}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="back-to-dashboard-btn"
              >
                Retour au tableau de bord
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit' }}>
                Erreur de paiement
              </h1>
              <p className="text-muted-foreground mb-6">
                Le paiement n'a pas pu être confirmé. Veuillez réessayer.
              </p>
              <Button 
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="rounded-full"
              >
                Retour au tableau de bord
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
